import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * Where the people in a kill race are actually playing from.
 *
 * The attendance curve says how many and when. This says where -- which is the
 * question behind the curve, because a race that empties at 9pm has emptied in
 * some particular timezone, and the fix ("run it two hours earlier for EU") is
 * only visible once the drop-off has a place attached to it.
 *
 * Drawn as an orthographic wireframe rather than a map: no tiles, no imagery,
 * no dependency. The coastlines are hand-cut and deliberately coarse -- enough
 * to know which continent a dot is on, which is all a 160-pixel globe can
 * usefully say. A spawn lights up where it came from, so a live race reads as
 * activity rather than as a table that happens to be changing.
 *
 * Click it for the breakdown -- regions with counts, each opening into the
 * countries under it.
 */
export type Country = { name: string; code: string; players: number; lat: number; lon: number }
export type Region = { key: string; name: string; countries: Country[] }

/** A spawn to light up: where it came from, and when it landed. */
export type Spark = { id: number; lat: number; lon: number; at: number }

const RADIUS = 78
const CENTRE = 90
const TILT = (18 * Math.PI) / 180
const RAD = Math.PI / 180

/** One colour per region, carried from the dot on the globe to the bar in the
 *  legend to the row in the breakdown, so the three read as one thing. */
const REGION_COLOUR: Record<string, string> = {
    na: '#4ade80',
    sa: '#facc15',
    eu: '#60a5fa',
    apac: '#f472b6',
    mena: '#fb923c',
    af: '#a78bfa',
    oce: '#22d3ee',
}

const colourOf = (key: string) => REGION_COLOUR[key] ?? '#4ade80'

/** Orthographic projection about a rotating meridian.
 *
 *  Also reports how far round the sphere the point sits: anything with a
 *  non-positive cosine is on the far side and must not be drawn, or the globe
 *  collapses into a flat disc of overlapping dots. */
const project = (lat: number, lon: number, spin: number) => {
    const p = lat * RAD
    const l = (lon - spin) * RAD
    const cosC = Math.sin(TILT) * Math.sin(p) + Math.cos(TILT) * Math.cos(p) * Math.cos(l)
    return {
        x: CENTRE + RADIUS * Math.cos(p) * Math.sin(l),
        y:
            CENTRE -
            RADIUS * (Math.cos(TILT) * Math.sin(p) - Math.sin(TILT) * Math.cos(p) * Math.cos(l)),
        front: cosC > 0,
        depth: cosC,
    }
}

/** A latitude or longitude line, broken wherever it passes round the back. */
const arc = (points: [number, number][], spin: number): string[] => {
    const runs: string[] = []
    let current: string[] = []
    for (const [lat, lon] of points) {
        const p = project(lat, lon, spin)
        if (p.front) {
            current.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        } else {
            if (current.length > 1) runs.push(current.join(' '))
            current = []
        }
    }
    if (current.length > 1) runs.push(current.join(' '))
    return runs
}

/** Coastlines, coarse on purpose.
 *
 *  Enough to know which continent a dot is sitting on, which is the whole job
 *  here -- a dot at 39N 98W means nothing until there is a North America under
 *  it. Anything more faithful would be a map dependency and a few hundred
 *  kilobytes to draw something 160 pixels wide.
 *
 *  [lon, lat] pairs, drawn as open lines rather than filled shapes: a filled
 *  polygon has to be clipped against the horizon, and a stroke can simply stop
 *  when it goes round the back.
 */
const LAND: [number, number][][] = [
    // North America
    [
        [-168, 65], [-160, 71], [-140, 70], [-125, 70], [-110, 68], [-95, 68],
        [-82, 73], [-62, 60], [-55, 52], [-65, 45], [-70, 41], [-75, 35],
        [-81, 25], [-97, 26], [-105, 20], [-110, 23], [-115, 30], [-125, 40],
        [-125, 48], [-135, 57], [-150, 59], [-165, 55], [-168, 65],
    ],
    // Greenland
    [
        [-45, 60], [-52, 68], [-60, 76], [-40, 83], [-20, 80], [-22, 70],
        [-32, 65], [-45, 60],
    ],
    // South America
    [
        [-81, 8], [-75, 11], [-62, 10], [-52, 5], [-44, -2], [-35, -5],
        [-38, -13], [-48, -25], [-58, -35], [-62, -40], [-65, -48], [-70, -55],
        [-75, -50], [-73, -40], [-71, -30], [-70, -18], [-75, -10], [-80, -5],
        [-81, 8],
    ],
    // Africa
    [
        [-17, 15], [-10, 28], [0, 35], [10, 37], [20, 32], [32, 31], [35, 23],
        [43, 12], [51, 12], [42, -2], [40, -10], [35, -20], [33, -27],
        [25, -34], [18, -35], [12, -18], [9, -2], [9, 4], [-5, 5], [-13, 9],
        [-17, 15],
    ],
    // Eurasia
    [
        [-10, 36], [-9, 43], [-2, 48], [2, 51], [5, 53], [8, 57], [11, 58],
        [18, 55], [22, 60], [28, 66], [30, 70], [45, 68], [60, 70], [75, 73],
        [90, 75], [105, 77], [120, 73], [135, 72], [145, 70], [160, 68],
        [170, 66], [178, 65], [170, 60], [160, 58], [150, 59], [142, 52],
        [135, 45], [128, 38], [122, 31], [115, 24], [108, 18], [105, 10],
        [100, 7], [98, 10], [92, 21], [88, 22], [80, 15], [77, 8], [72, 20],
        [68, 24], [60, 25], [56, 26], [50, 30], [43, 38], [35, 36], [28, 36],
        [20, 40], [15, 38], [12, 45], [5, 43], [-2, 43], [-10, 36],
    ],
    // British Isles
    [[-5, 50], [-6, 55], [-3, 58], [0, 54], [1, 51], [-5, 50]],
    // Japan
    [[130, 31], [135, 34], [140, 38], [142, 43], [145, 44], [141, 40], [138, 35], [133, 33], [130, 31]],
    // Australia
    [
        [113, -22], [114, -35], [125, -33], [135, -35], [147, -38], [153, -28],
        [146, -19], [142, -11], [132, -11], [125, -14], [117, -20], [113, -22],
    ],
    // New Zealand
    [[173, -35], [178, -38], [174, -41], [170, -45], [167, -46], [171, -42], [173, -35]],
]

/** Resampled so a long straight leg still bends round the sphere. Two points
 *  1,000km apart projected and joined by a straight line cuts a visible chord
 *  across the globe. */
const COAST: [number, number][][] = LAND.map((line) => {
    const out: [number, number][] = []
    for (let i = 0; i < line.length - 1; i += 1) {
        const [lon1, lat1] = line[i]
        const [lon2, lat2] = line[i + 1]
        const steps = Math.max(1, Math.ceil(Math.hypot(lon2 - lon1, lat2 - lat1) / 4))
        for (let k = 0; k < steps; k += 1) {
            out.push([lat1 + ((lat2 - lat1) * k) / steps, lon1 + ((lon2 - lon1) * k) / steps])
        }
    }
    const [lonN, latN] = line[line.length - 1]
    out.push([latN, lonN])
    return out
})

const GRATICULE = (() => {
    const lines: [number, number][][] = []
    for (let lat = -60; lat <= 60; lat += 30) {
        const ring: [number, number][] = []
        for (let lon = -180; lon <= 180; lon += 6) ring.push([lat, lon])
        lines.push(ring)
    }
    for (let lon = -180; lon < 180; lon += 30) {
        const ring: [number, number][] = []
        for (let lat = -90; lat <= 90; lat += 6) ring.push([lat, lon])
        lines.push(ring)
    }
    return lines
})()

export const PlayerGlobe = ({
    regions,
    sparks,
    live,
}: {
    regions: Region[]
    /** Recent spawns. Anything older than the flash is ignored, so the caller
     *  can keep a short rolling list and never has to prune precisely. */
    sparks?: Spark[]
    live?: boolean
}) => {
    const [spin, setSpin] = useState(0)
    const [open, setOpen] = useState(false)
    const [expanded, setExpanded] = useState<string[]>([])
    const [now, setNow] = useState(() => Date.now())
    const frame = useRef<number>()

    // One animation frame drives both the rotation and the flash decay. A
    // separate timer for the sparks would tick out of step with the frame that
    // actually draws them.
    useEffect(() => {
        let last = performance.now()
        const step = (t: number) => {
            const dt = t - last
            last = t
            setSpin((s) => (s + dt * 0.006) % 360)
            setNow(Date.now())
            frame.current = requestAnimationFrame(step)
        }
        frame.current = requestAnimationFrame(step)
        return () => {
            if (frame.current) cancelAnimationFrame(frame.current)
        }
    }, [])

    const totals = useMemo(
        () =>
            regions
                .map((r) => ({
                    ...r,
                    players: r.countries.reduce((s, c) => s + c.players, 0),
                }))
                .sort((a, b) => b.players - a.players),
        [regions],
    )

    const total = totals.reduce((s, r) => s + r.players, 0)

    const dots = useMemo(
        () =>
            regions.flatMap((r) =>
                r.countries
                    .filter((c) => c.players > 0)
                    .map((c) => ({ ...c, colour: colourOf(r.key) })),
            ),
        [regions],
    )

    const peak = Math.max(1, ...dots.map((d) => d.players))

    /** Flashes last 900ms: long enough to catch out of the corner of an eye,
     *  short enough that a busy server does not go solid white. */
    const flashes = (sparks ?? []).filter((s) => now - s.at < 900)

    return (
        <div className='flex flex-col gap-2'>
            <div className='flex flex-row items-baseline justify-between gap-3'>
                <span className='text-[10px] uppercase tracking-widest text-white/40'>
                    Where they are playing from
                </span>
                <span className='text-[10px] tabular-nums text-white/40'>
                    {total} players &middot; {totals.filter((r) => r.players > 0).length} regions
                </span>
            </div>

            <div
                role='button'
                tabIndex={0}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v)
                }}
                title='Click for the region breakdown'
                className='flex cursor-pointer flex-row items-center gap-4 rounded border border-white/10 bg-black/40 p-2 text-left hover:border-white/25'
            >
                <svg viewBox='0 0 180 180' className='h-40 w-40 shrink-0'>
                    <defs>
                        <radialGradient id='globe-sheen' cx='38%' cy='32%'>
                            <stop offset='0%' stopColor='rgba(74,222,128,0.14)' />
                            <stop offset='100%' stopColor='rgba(0,0,0,0)' />
                        </radialGradient>
                    </defs>

                    <circle cx={CENTRE} cy={CENTRE} r={RADIUS} fill='rgba(255,255,255,0.02)' />
                    <circle cx={CENTRE} cy={CENTRE} r={RADIUS} fill='url(#globe-sheen)' />
                    <circle
                        cx={CENTRE}
                        cy={CENTRE}
                        r={RADIUS}
                        fill='none'
                        stroke='rgba(74,222,128,0.35)'
                        strokeWidth={1}
                    />

                    {GRATICULE.map((ring, i) =>
                        arc(ring, spin).map((run, j) => (
                            <polyline
                                key={`${i}-${j}`}
                                points={run}
                                fill='none'
                                stroke='rgba(74,222,128,0.16)'
                                strokeWidth={0.6}
                            />
                        )),
                    )}

                    {COAST.map((line, i) =>
                        arc(line, spin).map((run, j) => (
                            <polyline
                                key={`c${i}-${j}`}
                                points={run}
                                fill='none'
                                stroke='rgba(74,222,128,0.55)'
                                strokeWidth={0.9}
                                strokeLinejoin='round'
                            />
                        )),
                    )}

                    {/* Population dots. Size is the share of the crowd, so the
                        big markets read at a glance without a legend. */}
                    {dots.map((d) => {
                        const p = project(d.lat, d.lon, spin)
                        if (!p.front) return null
                        return (
                            <circle
                                key={d.code}
                                cx={p.x}
                                cy={p.y}
                                r={1.6 + (d.players / peak) * 3.4}
                                fill={d.colour}
                                opacity={0.25 + p.depth * 0.65}
                            />
                        )
                    })}

                    {/* The country code, on whatever is squarely facing us.
                        Labelling the whole hemisphere turns the globe into a
                        thicket, and the ones near the edge are the ones about
                        to rotate away anyway. */}
                    {dots.map((d) => {
                        const p = project(d.lat, d.lon, spin)
                        if (!p.front || p.depth < 0.45) return null
                        return (
                            <text
                                key={`l${d.code}`}
                                x={p.x + 4 + (d.players / peak) * 3}
                                y={p.y + 2.5}
                                style={{ fontSize: 6.5 }}
                                fill={d.colour}
                                opacity={Math.min(1, (p.depth - 0.45) * 4)}
                            >
                                {d.code}
                            </text>
                        )
                    })}

                    {/* A spawn: a ring thrown out from the spot it happened. */}
                    {flashes.map((s) => {
                        const p = project(s.lat, s.lon, spin)
                        if (!p.front) return null
                        const age = (now - s.at) / 900
                        return (
                            <g key={s.id}>
                                <circle
                                    cx={p.x}
                                    cy={p.y}
                                    r={2 + age * 12}
                                    fill='none'
                                    stroke='rgba(255,255,255,0.9)'
                                    strokeWidth={1.2}
                                    opacity={1 - age}
                                />
                                <circle
                                    cx={p.x}
                                    cy={p.y}
                                    r={2.4}
                                    fill='#ffffff'
                                    opacity={1 - age * 0.8}
                                />
                            </g>
                        )
                    })}
                </svg>

                <div className='flex min-w-0 flex-1 flex-col gap-1'>
                    {totals.slice(0, 4).map((r) => (
                        <div key={r.key} className='flex flex-row items-center gap-2'>
                            <span
                                className='h-1.5 w-1.5 shrink-0 rounded-full'
                                style={{ background: colourOf(r.key) }}
                            />
                            <span className='w-24 shrink-0 truncate text-white/70'>{r.name}</span>
                            <span className='h-1 min-w-0 flex-1 rounded bg-white/5'>
                                <span
                                    className='block h-1 rounded'
                                    style={{
                                        width: `${total ? (r.players / total) * 100 : 0}%`,
                                        background: colourOf(r.key),
                                        opacity: 0.55,
                                    }}
                                />
                            </span>
                            <span className='w-16 shrink-0 text-right tabular-nums text-white/50'>
                                {r.players}{' '}
                                <span className='text-white/25'>
                                    {total ? Math.round((r.players / total) * 100) : 0}%
                                </span>
                            </span>
                        </div>
                    ))}
                    <span className='mt-1 text-[10px] text-white/30'>
                        {open ? 'Click to collapse' : 'Click for every country'}
                        {live ? ' · flashes are spawns as they happen' : ''}
                    </span>
                </div>
            </div>

            {open && (
                <div className='flex flex-col rounded border border-white/10'>
                    {totals.map((r) => {
                        const isOpen = expanded.includes(r.key)
                        return (
                            <div key={r.key} className='border-b border-white/5 last:border-b-0'>
                                <button
                                    type='button'
                                    onClick={() =>
                                        setExpanded((prev) =>
                                            prev.includes(r.key)
                                                ? prev.filter((k) => k !== r.key)
                                                : [...prev, r.key],
                                        )
                                    }
                                    className='flex w-full flex-row items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5'
                                >
                                    <span className='w-3 shrink-0 text-white/30'>
                                        {isOpen ? '-' : '+'}
                                    </span>
                                    <span
                                        className='h-1.5 w-1.5 shrink-0 rounded-full'
                                        style={{ background: colourOf(r.key) }}
                                    />
                                    <span className='min-w-0 flex-1 truncate'>{r.name}</span>
                                    <span className='shrink-0 text-white/30'>
                                        {r.countries.filter((c) => c.players > 0).length} countries
                                    </span>
                                    <span className='w-14 shrink-0 text-right tabular-nums'>
                                        {r.players}
                                    </span>
                                    <span className='w-10 shrink-0 text-right tabular-nums text-white/30'>
                                        {total ? Math.round((r.players / total) * 100) : 0}%
                                    </span>
                                </button>

                                {isOpen && (
                                    <div className='flex flex-col bg-black/30 pb-1'>
                                        {r.countries
                                            .filter((c) => c.players > 0)
                                            .sort((a, b) => b.players - a.players)
                                            .map((c) => (
                                                <div
                                                    key={c.code}
                                                    className='flex flex-row items-center gap-2 py-0.5 pl-10 pr-3'
                                                >
                                                    <span className='w-8 shrink-0 text-white/30'>
                                                        {c.code}
                                                    </span>
                                                    <span className='min-w-0 flex-1 truncate text-white/70'>
                                                        {c.name}
                                                    </span>
                                                    <span className='w-14 shrink-0 text-right tabular-nums text-white/60'>
                                                        {c.players}
                                                    </span>
                                                    <span className='w-10 shrink-0 text-right tabular-nums text-white/25'>
                                                        {r.players
                                                            ? Math.round(
                                                                  (c.players / r.players) * 100,
                                                              )
                                                            : 0}
                                                        %
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
